import { FormService, Logger } from '@core/services';
import { untilDestroyed } from '@ngneat/until-destroy';
import { EventEntity, EventsList, PaginatedEntity, Pagination } from '@core/entities';
import { inject } from '@angular/core';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { HotToastService } from '@ngxpert/hot-toast';
import { PermissionService } from '@auth';
import { UseClub, UseWorkShift } from '@core/usecases';
import { humanize, isMobileView, sortData } from '@core/utils';
import { ConfirmationDialogComponent, EventDetailDialogComponent } from '@web/_shared/dialogs';
import { ConfirmationDialogType, DeleteConfirmationOptions } from '@core/enums';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { CurrentSort } from '@core/types';

const log = new Logger('Work shifts Composition');

export class WorkShiftsComposition extends FormService {
  events: EventEntity[] = [];
  sortedEvents: EventEntity[] = [];
  pagination: Pagination = new PaginatedEntity({
    pagination: {
      page: 1,
      pageSize: 20,
    },
  }).pagination;

  currentSort: CurrentSort = { column: '', direction: '' };

  protected readonly _bottomSheetService: NgxBottomSheetService;
  protected readonly _dialogService: DialogService;
  protected _dialogRef: DialogRef;
  protected readonly _translateService = inject(TranslateService);
  protected readonly _router = inject(Router);
  protected readonly _toast = inject(HotToastService);
  protected readonly _permissionService = inject(PermissionService);

  private readonly _useWorkShift = new UseWorkShift();
  private readonly _useClub = new UseClub();

  constructor() {
    super();
    if (!isMobileView()) {
      this._dialogService = inject(DialogService);
    } else {
      this._bottomSheetService = inject(NgxBottomSheetService);
    }
  }

  onSortChange({ column, direction }: CurrentSort) {
    this.currentSort = { column, direction };
    this.sortedEvents = sortData(this.events, column as keyof (typeof this.events)[0], direction);
  }

  openEventDetails(event: EventEntity) {
    if (!isMobileView()) {
      this._dialogService.open(EventDetailDialogComponent, {
        size: 'md',
        closeButton: false,
        width: '620px',
        windowClass: 'dialog-overflow-unset dialog-overflow-y-auto',
        data: {
          id: event.id,
          event: event,
        },
      });
    }
  }

  deleteAllShiftsForEvent(event: EventEntity) {
    if (!event.id) return;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: humanize(event.title || event.name) + ' ' + this._translateService.instant('Work Shifts') + '?',
        },
      });

      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this.call(this._useWorkShift.deleteAllShifts(event.id)).then(() => {
                  this._toast.success(this._translateService.instant('Delete Successful'));
                  this._reloadCurrentRoute();
                });
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      const confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.SHIFTS,
          emphasize: event.title || event.name,
        },
      });
      confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (result: any) => {
          if (result) {
            this.call(this._useWorkShift.deleteAllShifts(event.id)).then(() => {
              this._toast.success(this._translateService.instant('Delete Successful'));
              this._reloadCurrentRoute();
            });
          }
        },
      });
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------
  protected paginationChange(pagination: Pagination) {
    this.pagination = pagination;
    this.isLoading = true;
    this._fetchAllWorkShifts();
  }

  protected _fetchAllWorkShifts() {
    this.call(this._useWorkShift.getAllEventsWithShifts(this.pagination))
      .then((res: EventsList) => {
        if (res) {
          this.events = res.events;
          this.sortedEvents = this.events;
          this.pagination = res.pagination;
          this.isLoading = false;

          if (isMobileView()) {
            window.scrollTo(0, 0);
          }
        } else {
          log.info('No Shifts Data found');
          this.isLoading = false;
        }
      })
      .catch((err) => {
        log.error('Error while fetching Shifts Details', err);
        this.isLoading = false;
      });
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }
}
